import html2canvas from 'html2canvas';

const CANVAS_SAFE_MAX_DIMENSION = 12288;

const PREFERRED_SCALE_CAP = 2.5;

const CLIPPING_OVERFLOW = new Set(['auto', 'scroll', 'hidden', 'clip']);

type OverflowStyleSnapshot = {
    element: HTMLElement;
    overflow: string;
    overflowX: string;
    overflowY: string;
    maxHeight: string;
    maxWidth: string;
    height: string;
    width: string;
};

/**
 * Временно снимает overflow на контейнерах, чтобы захватить полную высоту контента.
 * Возвращает снимки inline-стилей для восстановления.
 */
const expandOverflowContainers = (root: HTMLElement, view: Window): OverflowStyleSnapshot[] => {
    const snapshots: OverflowStyleSnapshot[] = [];
    const nodes: HTMLElement[] = [root, ...Array.from(root.querySelectorAll<HTMLElement>('*'))];

    nodes.forEach((el) => {
        const computed = view.getComputedStyle(el);
        const clipsY = CLIPPING_OVERFLOW.has(computed.overflowY) || CLIPPING_OVERFLOW.has(computed.overflow);
        const clipsX = CLIPPING_OVERFLOW.has(computed.overflowX) || CLIPPING_OVERFLOW.has(computed.overflow);

        if (!clipsX && !clipsY) {
            return;
        }

        snapshots.push({
            element: el,
            overflow: el.style.overflow,
            overflowX: el.style.overflowX,
            overflowY: el.style.overflowY,
            maxHeight: el.style.maxHeight,
            maxWidth: el.style.maxWidth,
            height: el.style.height,
            width: el.style.width,
        });

        el.style.overflow = 'visible';
        el.style.overflowX = 'visible';
        el.style.overflowY = 'visible';

        if (clipsY && computed.maxHeight !== 'none') {
            el.style.maxHeight = 'none';
        }

        if (clipsX && computed.maxWidth !== 'none') {
            el.style.maxWidth = 'none';
        }

        if (clipsY && el.scrollHeight > el.clientHeight) {
            el.style.height = `${el.scrollHeight}px`;
        }

        if (clipsX && el.scrollWidth > el.clientWidth) {
            el.style.width = `${el.scrollWidth}px`;
        }
    });

    return snapshots;
};

const restoreOverflowContainers = (snapshots: OverflowStyleSnapshot[]): void => {
    snapshots.forEach(({element, overflow, overflowX, overflowY, maxHeight, maxWidth, height, width}) => {
        element.style.overflow = overflow;
        element.style.overflowX = overflowX;
        element.style.overflowY = overflowY;
        element.style.maxHeight = maxHeight;
        element.style.maxWidth = maxWidth;
        element.style.height = height;
        element.style.width = width;
    });
};

/**
 * Адаптивный scale: предпочитаем min(dpr, PREFERRED_SCALE_CAP), но не превышаем лимит canvas.
 * Минимум 1 для читаемости; ниже 1 — только если иначе canvas не влезет.
 */
export const calculateScreenshotScale = (contentWidth: number, contentHeight: number): number => {
    const devicePixelRatio = window.devicePixelRatio || 1;
    const preferredScale = Math.min(devicePixelRatio, PREFERRED_SCALE_CAP);

    const scaleByWidth = CANVAS_SAFE_MAX_DIMENSION / contentWidth;
    const scaleByHeight = CANVAS_SAFE_MAX_DIMENSION / contentHeight;
    const fitScale = Math.min(scaleByWidth, scaleByHeight);

    let scale = Math.min(preferredScale, fitScale);
    scale = Math.max(scale, 1);

    if (contentWidth * scale > CANVAS_SAFE_MAX_DIMENSION || contentHeight * scale > CANVAS_SAFE_MAX_DIMENSION) {
        scale = fitScale;
    }

    return scale;
};

export const captureElementScreenshot = async (element: HTMLElement): Promise<HTMLCanvasElement> => {
    const overflowSnapshots = expandOverflowContainers(element, window);

    try {
        void element.offsetHeight;

        const width = element.scrollWidth;
        const height = element.scrollHeight;
        const scale = calculateScreenshotScale(width, height);

        return await html2canvas(element, {
            backgroundColor: '#ffffff',
            useCORS: true,
            scale,
            width,
            height,
            windowWidth: width,
            windowHeight: height,
            onclone: (clonedDocument, clonedElement) => {
                const cloneView = clonedDocument.defaultView;

                if (cloneView) {
                    expandOverflowContainers(clonedElement, cloneView);
                }
            },
        });
    } finally {
        restoreOverflowContainers(overflowSnapshots);
    }
};

export const captureElementScreenshotBlob = async (element: HTMLElement): Promise<Blob> => {
    const canvas = await captureElementScreenshot(element);

    return new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((result: Blob | null) => {
            if (result) {
                resolve(result);
                return;
            }

            reject(new Error('Не удалось создать изображение'));
        }, 'image/png');
    });
};
